// pages/TiposIVA.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Spin,
  Card,
  InputNumber,
  DatePicker,
  Tabs,
  List,
  Descriptions
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  PercentageOutlined,
  ExportOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  CalculatorOutlined,
  DollarOutlined,
  TagOutlined,
  CalendarOutlined,
  HistoryOutlined,
  PlusCircleOutlined,
  DeleteFilled
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const TiposIVA = () => {
  // Estados principales
  const [tiposIVA, setTiposIVA] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentTipoIVA, setCurrentTipoIVA] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  // Estados para valores/historial
  const [valoresIVA, setValoresIVA] = useState([]);
  const [loadingValores, setLoadingValores] = useState(false);
  const [modalValoresVisible, setModalValoresVisible] = useState(false);
  const [formValores] = Form.useForm();
  const [tipoIVAValores, setTipoIVAValores] = useState(null);
  const [modalTypeValores, setModalTypeValores] = useState('create');

  // Estados para filtros y ordenación
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  // Estado para paginación
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} tipos de IVA`
  });

  // Configuración de columnas de la tabla
  const columns = [
    {
      title: 'Código',
      dataIndex: 'Codigo',
      key: 'Codigo',
      sorter: (a, b) => a.Codigo.localeCompare(b.Codigo),
      sortOrder: sortedInfo.columnKey === 'Codigo' && sortedInfo.order,
      width: 100,
      render: (codigo) => (
        <Tag color="blue" style={{ fontWeight: 'bold', fontSize: '14px' }}>
          {codigo}
        </Tag>
      ),
    },
    {
      title: 'Descripción',
      dataIndex: 'Descripcion',
      key: 'Descripcion',
      sorter: (a, b) => a.Descripcion.localeCompare(b.Descripcion),
      sortOrder: sortedInfo.columnKey === 'Descripcion' && sortedInfo.order,
      width: 200,
      ellipsis: true,
      render: (descripcion, record) => (
        <div>
          <strong>{descripcion}</strong>
          {record.EsPredeterminado && (
            <Tag color="gold" style={{ marginLeft: 8, fontSize: '10px' }}>
              Predet.
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Valor Actual (%)',
      key: 'valorActual',
      width: 150,
      render: (_, record) => {
        // Obtener el valor actual (el más reciente)
        const valorActual = record.ValorActual || 0;
        let color = 'default';
        if (valorActual === 0) color = 'gray';
        else if (valorActual <= 10) color = 'green';
        else if (valorActual <= 21) color = 'orange';
        else color = 'red';
        
        return (
          <Badge
            count={`${valorActual}%`}
            style={{ 
              backgroundColor: color,
              fontSize: '12px',
              fontWeight: 'bold'
            }}
          />
        );
      },
    },
    {
      title: 'Código Contable',
      dataIndex: 'CodConta',
      key: 'CodConta',
      width: 140,
      render: (codConta) => (
        codConta ? (
          <Tag color="purple">{codConta}</Tag>
        ) : (
          <span style={{ color: '#999' }}>No asignado</span>
        )
      ),
    },
    {
      title: 'Historial',
      key: 'historial',
      width: 120,
      render: (_, record) => (
        <Button
          type="default"
          icon={<HistoryOutlined />}
          size="small"
          onClick={() => handleVerHistorial(record)}
        >
          Valores
        </Button>
      ),
    },
    {
      title: 'Activo',
      dataIndex: 'Activo',
      key: 'Activo',
      width: 90,
      render: (activo) => (
        activo ? (
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
        ) : (
          <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} />
        )
      ),
      filters: [
        { text: 'Activo', value: true },
        { text: 'Inactivo', value: false },
      ],
      filteredValue: filteredInfo.Activo || null,
      onFilter: (value, record) => record.Activo === value,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
          
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Eliminar tipo de IVA?"
            description="Esta acción eliminará el tipo de IVA y su historial permanentemente"
            onConfirm={() => handleDelete(record.Codigo)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                disabled={record.EsPredeterminado}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Cargar datos iniciales
  useEffect(() => {
    fetchTiposIVA();
  }, []);

  const fetchTiposIVA = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Parámetros de la query
      const queryParams = {
        page: params.current || pagination.current,
        pageSize: params.pageSize || pagination.pageSize,
        search: searchText,
        sortField: params.sortField || sortedInfo.field || 'Codigo',
        sortOrder: params.sortOrder || (sortedInfo.order === 'descend' ? 'DESC' : 'ASC')
      };
      
      // Añadir filtros
      if (filteredInfo.Activo) {
        queryParams.Activo = filteredInfo.Activo[0];
      }
      if (filteredInfo.EsPredeterminado) {
        queryParams.EsPredeterminado = filteredInfo.EsPredeterminado[0];
      }
      
      const response = await axios.get(`${BASE_URL}/api/tiposiva`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });

      const cleanedData = (response.data.data || []).map(item => ({
        ...item,
        CodConta: item.CodConta || '',
        Activo: Boolean(item.Activo),
        EsPredeterminado: Boolean(item.EsPredeterminado),
        ValorActual: item.ValorActual || 0
      }));
      
      setTiposIVA(cleanedData);
      
      // Actualizar paginación
      setPagination({
        ...pagination,
        current: response.data.pagination?.currentPage || 1,
        pageSize: response.data.pagination?.pageSize || pagination.pageSize,
        total: response.data.pagination?.totalRecords || cleanedData.length
      });
      
    } catch (error) {
      console.error('❌ Error cargando tipos de IVA:', error);
      message.error('Error al cargar los tipos de IVA');
      setTiposIVA([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const fetchValoresIVA = async (codigoTipoIVA) => {
    setLoadingValores(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/tiposiva/${codigoTipoIVA}/valores`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setValoresIVA(response.data.data || []);
    } catch (error) {
      console.error('❌ Error cargando valores de IVA:', error);
      message.error('Error al cargar los valores de IVA');
      setValoresIVA([]);
    } finally {
      setLoadingValores(false);
    }
  };

  const handleTableChange = (newPagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    
    const params = {
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      sortField: sorter.field,
      sortOrder: sorter.order === 'ascend' ? 'ASC' : 'DESC'
    };
    
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
    
    fetchTiposIVA(params);
  };

  // Handlers para las acciones principales
  const handleCreate = async () => {
    setModalType('create');
    setCurrentTipoIVA(null);
    form.resetFields();
    
    // Establecer valores por defecto
    form.setFieldsValue({
      Activo: true,
      EsPredeterminado: false
    });
    
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentTipoIVA(record);
    
    const formValues = {
      Codigo: record.Codigo,
      Descripcion: record.Descripcion || '',
      CodConta: record.CodConta || '',
      Activo: record.Activo,
      EsPredeterminado: record.EsPredeterminado || false
    };
    
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handleVerHistorial = (record) => {
    setTipoIVAValores(record);
    fetchValoresIVA(record.Codigo);
    setModalValoresVisible(true);
  };

  const handleNuevoValor = () => {
    setModalTypeValores('create');
    formValores.resetFields();
    formValores.setFieldsValue({
      FechaValidez: dayjs(),
      Valor: 21
    });
  };

  const handleGuardarValor = async () => {
    try {
      const values = await formValores.validateFields();
      const token = localStorage.getItem('token');
      
      const valorData = {
        ...values,
        FechaValidez: values.FechaValidez.format('YYYY-MM-DD')
      };
      
      if (modalTypeValores === 'create') {
        await axios.post(
          `${BASE_URL}/api/tiposiva/${tipoIVAValores.Codigo}/valores`,
          valorData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Valor de IVA creado correctamente');
      } else {
        await axios.put(
          `${BASE_URL}/api/tiposiva/${tipoIVAValores.Codigo}/valores/${currentValorIVA.i}`,
          valorData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Valor de IVA actualizado correctamente');
      }
      
      fetchValoresIVA(tipoIVAValores.Codigo);
      fetchTiposIVA(); // Actualizar el valor actual
      formValores.resetFields();
      setCurrentValorIVA(null);
      
    } catch (error) {
      console.error('❌ Error guardando valor de IVA:', error);
      message.error(error.response?.data?.error || 'Error al guardar el valor');
    }
  };

  const handleEliminarValor = async (i) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BASE_URL}/api/tiposiva/${tipoIVAValores.Codigo}/valores/${i}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      message.success('Valor de IVA eliminado correctamente');
      fetchValoresIVA(tipoIVAValores.Codigo);
      fetchTiposIVA(); // Actualizar el valor actual
      
    } catch (error) {
      console.error('❌ Error eliminando valor de IVA:', error);
      message.error(error.response?.data?.error || 'Error al eliminar el valor');
    }
  };

  const handleView = (record) => {
    Modal.info({
      title: `Tipo de IVA: ${record.Descripcion}`,
      width: 700,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <strong>Código:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="blue" style={{ fontSize: '16px' }}>
                  {record.Codigo}
                </Tag>
              </div>
            </Col>
            <Col span={8}>
              <strong>Valor Actual:</strong>
              <div style={{ marginTop: 4 }}>
                <Badge
                  count={`${record.ValorActual || 0}%`}
                  style={{ 
                    backgroundColor: (record.ValorActual || 0) === 0 ? 'gray' : 
                                  (record.ValorActual || 0) <= 10 ? 'green' : 
                                  (record.ValorActual || 0) <= 21 ? 'orange' : 'red',
                    fontSize: '16px',
                    padding: '8px 16px'
                  }}
                />
              </div>
            </Col>
            <Col span={8}>
              <strong>Estado:</strong>
              <div style={{ marginTop: 4 }}>
                {record.Activo ? (
                  <Tag color="green">Activo</Tag>
                ) : (
                  <Tag color="red">Inactivo</Tag>
                )}
              </div>
            </Col>
            
            <Col span={24}>
              <strong>Descripción:</strong>
              <div style={{ marginTop: 4, padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <strong>{record.Descripcion}</strong>
              </div>
            </Col>
            
            <Col span={12}>
              <strong>Código Contable:</strong>
              <div style={{ marginTop: 4 }}>
                {record.CodConta ? (
                  <Tag color="purple" style={{ fontSize: '14px', padding: '4px 8px' }}>
                    {record.CodConta}
                  </Tag>
                ) : (
                  <span style={{ color: '#999' }}>No asignado</span>
                )}
              </div>
            </Col>
            
            <Col span={12}>
              <strong>Es Predeterminado:</strong>
              <div style={{ marginTop: 4 }}>
                {record.EsPredeterminado ? (
                  <Tag color="gold" style={{ fontWeight: 'bold' }}>
                    <CheckCircleOutlined /> Sí
                  </Tag>
                ) : (
                  <Tag color="default">No</Tag>
                )}
              </div>
            </Col>
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const handleDelete = async (codigo) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BASE_URL}/api/tiposiva/${codigo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Filtrar el eliminado
      setTiposIVA(tiposIVA.filter(t => t.Codigo !== codigo));
      
      // Actualizar total
      setPagination(prev => ({
        ...prev,
        total: prev.total - 1
      }));
      
      message.success('Tipo de IVA eliminado correctamente');
    } catch (error) {
      console.error('❌ Error eliminando tipo de IVA:', error);
      message.error(error.response?.data?.error || 'Error al eliminar el tipo de IVA');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');

      // Si se marca como predeterminado, desmarcar los demás
      if (values.EsPredeterminado) {
        const existePredeterminado = tiposIVA.find(t => t.EsPredeterminado && t.Codigo !== currentTipoIVA?.Codigo);
        
        if (existePredeterminado && modalType === 'create') {
          message.warning('Ya existe un tipo de IVA predeterminado');
          return;
        }
      }

      if (modalType === 'create') {
        const response = await axios.post(
          `${BASE_URL}/api/tiposiva`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Si es predeterminado, actualizar el estado local de los demás
        if (values.EsPredeterminado) {
          setTiposIVA(prev => 
            prev.map(t => ({ ...t, EsPredeterminado: false }))
          );
        }
        
        fetchTiposIVA();
        message.success('Tipo de IVA creado correctamente');
      } else {
        const response = await axios.put(
          `${BASE_URL}/api/tiposiva/${currentTipoIVA.Codigo}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Actualizar el estado local
        const tipoIVAActualizado = {
          ...response.data.data,
          ...values
        };
        
        // Si es predeterminado, desmarcar los demás
        if (values.EsPredeterminado) {
          setTiposIVA(prev => 
            prev.map(t => 
              t.Codigo === currentTipoIVA.Codigo 
                ? tipoIVAActualizado 
                : { ...t, EsPredeterminado: false }
            )
          );
        } else {
          setTiposIVA(prev => 
            prev.map(t => 
              t.Codigo === currentTipoIVA.Codigo ? tipoIVAActualizado : t
            )
          );
        }
        
        message.success('Tipo de IVA actualizado correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
      
    } catch (error) {
      console.error('❌ Error guardando tipo de IVA:', error);
      message.error(error.response?.data?.error || 'Error al guardar los datos');
    }
  };

  const handleSearch = () => {
    if (searchText.trim() === '') {
      message.warning('Por favor ingrese un término de búsqueda');
      return;
    }
    setSearchLoading(true);
    setPagination({ ...pagination, current: 1 });
    fetchTiposIVA({ current: 1 });
  };

  const handleResetSearch = () => {
    setSearchText('');
    setFilteredInfo({});
    setSortedInfo({});
    setSearchLoading(false);
    setPagination({ ...pagination, current: 1 });
    fetchTiposIVA({ current: 1 });
  };

  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space>
        <Input
          placeholder="Buscar por código, descripción..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 350 }}
          onPressEnter={handleSearch}
          allowClear
          onClear={handleResetSearch}
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          loading={searchLoading}
        >
          Buscar
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleResetSearch}
        >
          Restablecer
        </Button>
        <span style={{ color: '#666', fontSize: '12px' }}>
          {pagination.total.toLocaleString()} tipos de IVA
        </span>
      </Space>
    </div>
  );

  const handleExport = () => {
    message.info('Función de exportación en desarrollo');
  };

  // Estadísticas
  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={6}>
        <Statistic 
          title="Total Tipos IVA" 
          value={pagination.total}
          prefix={<PercentageOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Activos" 
          value={tiposIVA.filter(t => t.Activo).length}
          prefix={<CheckCircleOutlined />}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Históricos" 
          value={valoresIVA.length}
          prefix={<HistoryOutlined />}
          valueStyle={{ color: '#fa8c16' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Predeterminados" 
          value={tiposIVA.filter(t => t.EsPredeterminado).length}
          prefix={<TagOutlined />}
          valueStyle={{ color: '#f5222d' }}
        />
      </Col>
    </Row>
  );

  // Columnas para tabla de valores
  const columnsValores = [
    {
      title: 'Fecha Validez',
      dataIndex: 'FechaValidez',
      key: 'FechaValidez',
      width: 150,
      render: (fecha) => dayjs(fecha).format('DD/MM/YYYY')
    },
    {
      title: 'Valor (%)',
      dataIndex: 'Valor',
      key: 'Valor',
      width: 120,
      render: (valor) => {
        let color = 'default';
        if (valor === 0) color = 'gray';
        else if (valor <= 10) color = 'green';
        else if (valor <= 21) color = 'orange';
        else color = 'red';
        
        return (
          <Badge
            count={`${valor}%`}
            style={{ 
              backgroundColor: color,
              fontSize: '12px',
              fontWeight: 'bold'
            }}
          />
        );
      }
    },
    {
      title: 'Hotel',
      dataIndex: 'CodigoHotel',
      key: 'CodigoHotel',
      width: 100,
      render: (hotel) => (
        <Tag color="cyan">Hotel {hotel}</Tag>
      )
    },
    {
      title: 'Acciones',
      key: 'accionesValores',
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip title="Eliminar">
            <Button
              danger
              icon={<DeleteFilled />}
              size="small"
              onClick={() => handleEliminarValor(record.i)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <>
      <PageContainer
        title="Tipos de IVA"
        subtitle={`${pagination.total.toLocaleString()} tipos de IVA registrados`}
        icon={<PercentageOutlined />}
        loading={loading}
        onRefresh={() => fetchTiposIVA()}
        onCreate={handleCreate}
        badgeCount={pagination.total}
        badgeStatus="processing"
        extra={
          <Space>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
            >
              Exportar
            </Button>
            <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
              <strong>{pagination.total.toLocaleString()}</strong> tipos de IVA
            </Tag>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Estadísticas */}
        {estadisticas}
        
        {/* Barra de búsqueda */}
        {searchBar}
        
        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={tiposIVA}
          rowKey={(record) => record.Codigo}
          loading={loading}
          onChange={handleTableChange}
          pagination={pagination}
          scroll={{ x: 1200, y: 600 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
          size="middle"
          locale={{
            emptyText: 'No se encontraron tipos de IVA'
          }}
        />
      </PageContainer>

      {/* Modal para crear/editar tipo de IVA */}
      <Modal
        title={modalType === 'create' ? 'Nuevo Tipo de IVA' : 'Editar Tipo de IVA'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={500}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            Activo: true,
            EsPredeterminado: false
          }}
        >
          {/* Código */}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="Codigo"
                label="Código"
                rules={[{ 
                  required: true, 
                  message: 'Por favor ingrese el código'
                }]}
              >
                <Input 
                  placeholder="Ej: 0, 1, 2, R, G"
                  maxLength={10}
                  disabled={modalType === 'edit'}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Descripción */}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="Descripcion"
                label="Descripción"
                rules={[{ 
                  required: true, 
                  message: 'Por favor ingrese la descripción'
                }]}
              >
                <Input 
                  placeholder="Ej: IVA REDUCIDO, IVA NORMAL, EXENTO"
                  maxLength={100}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Código Contable */}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="CodConta"
                label="Código Contable"
              >
                <Input 
                  placeholder="Código contable para integración"
                  maxLength={20}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Switches en una fila */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="Activo"
                label="Activo"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="EsPredeterminado"
                label="Es Predeterminado"
                valuePropName="checked"
                tooltip="Este será el tipo de IVA por defecto en nuevas facturas"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Col>
          </Row>

          {modalType === 'create' && (
            <Card size="small" style={{ marginTop: 16, backgroundColor: '#f6ffed' }}>
              <div style={{ fontSize: '13px', color: '#666' }}>
                <strong>📝 Nota:</strong> El código debe ser único.
                <div style={{ marginTop: 8 }}>
                  <strong>Importante:</strong> Los valores de IVA (porcentajes) se configuran 
                  en el historial de valores de cada tipo.
                </div>
                <div style={{ marginTop: 8 }}>
                  <strong>Solo puede haber un tipo de IVA predeterminado.</strong> 
                  Al marcar este como predeterminado, se desmarcará automáticamente el anterior.
                </div>
              </div>
            </Card>
          )}
        </Form>
      </Modal>

      {/* Modal para gestionar valores de IVA */}
      <Modal
        title={`Historial de Valores - ${tipoIVAValores?.Descripcion} (${tipoIVAValores?.Codigo})`}
        open={modalValoresVisible}
        onCancel={() => {
          setModalValoresVisible(false);
          setTipoIVAValores(null);
          setValoresIVA([]);
          formValores.resetFields();
        }}
        width={800}
        footer={null}
        style={{ top: 20 }}
      >
        <Tabs defaultActiveKey="1">
          <TabPane tab="Historial de Valores" key="1">
            <div style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                icon={<PlusCircleOutlined />}
                onClick={handleNuevoValor}
              >
                Nuevo Valor
              </Button>
            </div>
            
            <Table
              columns={columnsValores}
              dataSource={valoresIVA}
              rowKey="i"
              loading={loadingValores}
              pagination={{ pageSize: 10 }}
              size="small"
              locale={{
                emptyText: 'No hay valores registrados para este tipo de IVA'
              }}
            />
          </TabPane>
          
          <TabPane tab="Agregar/Editar Valor" key="2">
            <Form
              form={formValores}
              layout="vertical"
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="FechaValidez"
                    label="Fecha de Validez"
                    rules={[{ 
                      required: true, 
                      message: 'Por favor seleccione la fecha de validez'
                    }]}
                  >
                    <DatePicker 
                      style={{ width: '100%' }}
                      format="DD/MM/YYYY"
                    />
                  </Form.Item>
                </Col>
                
                <Col span={12}>
                  <Form.Item
                    name="Valor"
                    label="Valor (%)"
                    rules={[{ 
                      required: true, 
                      message: 'Por favor ingrese el valor',
                      type: 'number',
                      min: 0,
                      max: 100
                    }]}
                  >
                    <InputNumber 
                      style={{ width: '100%' }}
                      min={0}
                      max={100}
                      step={0.01}
                      precision={2}
                      placeholder="0.00"
                      addonAfter="%"
                    />
                  </Form.Item>
                </Col>
              </Row>
              
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="CodigoHotel"
                    label="Hotel"
                    rules={[{ 
                      required: true, 
                      message: 'Por favor ingrese el código del hotel',
                      type: 'integer',
                      min: 1
                    }]}
                  >
                    <InputNumber 
                      style={{ width: '100%' }}
                      min={1}
                      placeholder="Código del hotel"
                    />
                  </Form.Item>
                </Col>
                
                <Col span={12}>
                  <Form.Item
                    name="CodConta"
                    label="Código Contable (opcional)"
                  >
                    <Input 
                      placeholder="Código contable específico"
                      maxLength={20}
                    />
                  </Form.Item>
                </Col>
              </Row>
              
              <div style={{ textAlign: 'right', marginTop: 16 }}>
                <Button
                  type="primary"
                  onClick={handleGuardarValor}
                >
                  {modalTypeValores === 'create' ? 'Crear Valor' : 'Guardar Cambios'}
                </Button>
              </div>
            </Form>
          </TabPane>
        </Tabs>
      </Modal>
    </>
  );
};

export default TiposIVA;